import { BRAND_COLORS } from "@/Utils/brandColors";
import "./styles.scss";
import React, { useState, useEffect } from "react";

type PageLayoutProps = {
  children: React.ReactNode;
};

const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
  return <div className="page-layout">{children}</div>;
};

type AvatarBoxProps = {
  title: string;
  description: React.ReactNode;
  isOutlined?: boolean;
};

const AvatarBox: React.FC<AvatarBoxProps> = ({
  title,
  description,
  isOutlined,
}) => {
  return (
    <div className={`avatar-box ${isOutlined ? "outlined" : "normal"}`}>
      <div className="avatar-box-header">
        <h1 className="h1">{title}</h1>
        <p className="p1">{description}</p>
      </div>
    </div>
  );
};

type AnimatedFigureIconProps = {
  color: string;
  toColor?: string | null;
  fillPercent?: number;
  submitted?: boolean;
  frozen?: boolean;
};

const AnimatedFigureIcon: React.FC<AnimatedFigureIconProps> = ({
  color,
  toColor: toColorProp,
  fillPercent = 0,
  submitted = false,
  frozen = false,
}) => {
  const [currentFill, setCurrentFill] = useState(0);
  const toColor = submitted ? BRAND_COLORS.success : toColorProp;

  useEffect(() => {
    if (frozen) return;
    if (toColor) {
      setCurrentFill(fillPercent);
      if (submitted) {
        setCurrentFill(100);
      }
    }
  }, [submitted, fillPercent, toColor, frozen]);

  return (
    <svg
      width="131"
      height="148"
      viewBox="0 0 131 148"
      className="animated-figure-icon"
    >
      <defs>
        <linearGradient id="fillGradient" x1="0" x2="0" y1="1" y2="0">
          <stop offset={`${currentFill}%`} stopColor={toColor || color} />
          <stop offset={`${currentFill}%`} stopColor={color} />
        </linearGradient>
      </defs>
      <path
        className="figure-path"
        fill="url(#fillGradient)"
        d="M73.6539 11.45C73.6539 14.093 72.6567 17.5031 70.9929 20.2295C69.2528 23.081 67.3218 24.371 65.8189 24.371C64.3161 24.371 62.3851 23.081 60.645 20.2295C58.9812 17.5031 57.984 14.093 57.984 11.45C57.984 6.25992 61.7266 2.5 65.8189 2.5C69.9113 2.5 73.6539 6.25992 73.6539 11.45ZM65.8189 26.871C71.5268 26.871 76.1539 17.7736 76.1539 11.45C76.1539 5.12632 71.5268 0 65.8189 0C60.1111 0 55.484 5.12632 55.484 11.45C55.484 17.7736 60.1111 26.871 65.8189 26.871ZM130.097 34.9014H0.904297V37.4014H130.097V34.9014ZM37.481 143.322C39.9622 126.061 43.2004 103.373 45.8184 85.0007C47.1273 75.8144 48.2812 67.7069 49.1079 61.8959C49.5212 58.9904 49.8528 56.6591 50.0809 55.0541L50.343 53.2104L50.4109 52.7329L50.4282 52.6114L50.4325 52.5808L50.4336 52.5731L50.4339 52.5711C50.434 52.5707 50.434 52.5705 49.1964 52.3946C47.9589 52.2188 47.9588 52.219 47.9588 52.2194L47.9585 52.2213L47.9574 52.229L47.9531 52.2596L47.9358 52.3811L47.8679 52.8586L47.6058 54.7022C47.3777 56.3071 47.0461 58.6384 46.6328 61.5438C45.8062 67.3546 44.6523 75.462 43.3434 84.648C40.7255 103.02 37.4874 125.707 35.0064 142.967C34.6541 145.418 36.117 147.19 37.9818 147.653C39.8387 148.115 41.9693 147.249 42.8498 144.947L61.4726 96.2654C62.154 94.4842 63.8634 93.3079 65.7705 93.3079C67.6734 93.3079 69.38 94.4791 70.0644 96.2547L88.4143 143.868C89.3104 146.193 91.4805 147.03 93.3456 146.552C95.2164 146.072 96.7054 144.287 96.3317 141.814C93.7362 124.638 90.4922 102.241 87.8969 84.1348C86.5992 75.0824 85.4639 67.1036 84.6529 61.3879C84.2475 58.5301 83.9231 56.2381 83.7001 54.6606L83.4442 52.8488L83.378 52.3796L83.3611 52.2603L83.3569 52.2302L83.3558 52.2227L83.3556 52.2208C83.3555 52.2204 83.3555 52.2202 82.1177 52.3946C80.8799 52.5691 80.88 52.5693 80.88 52.5697L80.8803 52.5716L80.8814 52.5792L80.8856 52.6093L80.9025 52.7287L80.9687 53.1981L81.2247 55.0105C81.4477 56.5884 81.7722 58.8808 82.1777 61.7391C82.9888 67.4557 84.1243 75.4357 85.4221 84.4896C88.0177 102.596 91.2627 125.002 93.8598 142.188C94.0316 143.325 93.4196 143.952 92.7246 144.13C92.024 144.31 91.1639 144.051 90.7471 142.969L72.3971 95.3557C71.341 92.6155 68.7072 90.8079 65.7705 90.8079C62.8273 90.8079 60.1892 92.6233 59.1376 95.3722L40.5148 144.054C40.0989 145.141 39.2521 145.393 38.5847 145.227C37.9253 145.063 37.3171 144.463 37.481 143.322Z"
      />
    </svg>
  );
};

export { PageLayout, AvatarBox, AnimatedFigureIcon };
